import { useCallback, useState } from 'react';
import { Box, Flex, Spinner, Text, chakra } from '@chakra-ui/react';
import { ArrowRight, Bell, X } from 'lucide-react';
import type { AppNotification } from '@/hooks/useNotifications';
import {
  MarkAllReadButton,
  NotificationRow,
  NotificationSection,
  useRowRemoval,
} from '@/components/common/NotificationRow';

/** The panel is a glance; the full page holds the rest. */
const PANEL_LIMIT = 20;

interface NotificationPanelProps {
  variant: 'popover' | 'sheet';
  titleId: string;
  notifications: AppNotification[];
  loading: boolean;
  error: string | null;
  onActivate: (n: AppNotification) => void;
  onDelete: (id: string) => Promise<{ ok: boolean }>;
  onMarkAllRead: () => Promise<unknown>;
  onSeeAll: () => void;
  onClose: () => void;
}

export function NotificationPanel({
  variant,
  titleId,
  notifications,
  loading,
  error,
  onActivate,
  onDelete,
  onMarkAllRead,
  onSeeAll,
  onClose,
}: NotificationPanelProps) {
  const sheet = variant === 'sheet';
  const [openSwipeId, setOpenSwipeId] = useState<string | null>(null);

  // Deleted rows stay on screen until their exit animation has played.
  const { rows, exitingIds, removeAnimated } = useRowRemoval(
    notifications,
    onDelete
  );
  const items = rows.slice(0, PANEL_LIMIT);
  const fresh = items.filter((n) => !n.read);
  const earlier = items.filter((n) => n.read);
  const newCount = notifications.filter((n) => !n.read).length;

  const activate = useCallback(
    (n: AppNotification) => {
      // While a row is swiped open, the first tap anywhere just closes it.
      if (openSwipeId) {
        setOpenSwipeId(null);
        return;
      }
      onActivate(n);
    },
    [openSwipeId, onActivate]
  );

  const handleDelete = useCallback(
    async (id: string) => {
      await removeAnimated(id);
      setOpenSwipeId(null);
    },
    [removeAnimated]
  );

  const renderRows = (rows: AppNotification[]) =>
    rows.map((n) => (
      <NotificationRow
        key={n.id}
        notification={n}
        onActivate={activate}
        onDelete={(id) => void handleDelete(id)}
        swipeOpen={openSwipeId === n.id}
        exiting={exitingIds.has(n.id)}
        onSwipeOpenChange={(open) =>
          setOpenSwipeId((cur) => (open ? n.id : cur === n.id ? null : cur))
        }
      />
    ));

  return (
    <Flex direction="column" minH="0" h="full" maxH="inherit">
      {/* On the sheet, the grabber and header are the drag handle (BottomSheet). touch-action
          none keeps the browser from scrolling or pull-to-refreshing under the drag. */}
      {sheet && (
        <Flex
          justify="center"
          pt="2"
          flexShrink={0}
          aria-hidden
          data-sheet-drag-handle
          touchAction="none"
        >
          <Box w="36px" h="5px" borderRadius="full" bg="volyx.borderLight" />
        </Flex>
      )}

      <Flex
        flexShrink={0}
        align="center"
        justify="space-between"
        gap="3"
        px="4"
        pt={sheet ? 2.5 : 4}
        pb="3"
        data-sheet-drag-handle={sheet || undefined}
        touchAction={sheet ? 'none' : undefined}
      >
        <Flex align="center" gap="2" minW="0">
          <Text
            id={titleId}
            m="0"
            fontSize={sheet ? '20px' : '17px'}
            lineHeight="24px"
            fontWeight="bold"
            color="volyx.text"
          >
            Notifications
          </Text>
          {newCount > 0 && (
            <Box
              as="span"
              px="2"
              py="0.5"
              borderRadius="full"
              bg="volyx.action"
              color="white"
              fontSize="13px"
              lineHeight="18px"
              fontWeight="semibold"
            >
              {newCount} new
            </Box>
          )}
        </Flex>
        {sheet && (
          <chakra.button
            type="button"
            aria-label="Close"
            display="flex"
            alignItems="center"
            justifyContent="center"
            w="32px"
            h="32px"
            flexShrink={0}
            border="none"
            borderRadius="full"
            bg="volyx.lightGray"
            color="volyx.textMuted"
            cursor="pointer"
            onClick={onClose}
          >
            <X size={16} aria-hidden />
          </chakra.button>
        )}
      </Flex>

      <Box
        flex="1"
        minH="0"
        overflowY="auto"
        overscrollBehavior="contain"
        borderTop="1px solid"
        borderBottom="1px solid"
        borderColor="volyx.borderLight"
      >
        {loading && items.length === 0 && (
          <Flex py="10" justify="center">
            <Spinner size="md" color="volyx.action" />
          </Flex>
        )}
        {!loading && error && (
          <Text m="0" px="4" py="4" fontSize="sm" color="red.500">
            {error}
          </Text>
        )}
        {!loading && !error && items.length === 0 && (
          <Flex
            direction="column"
            align="center"
            textAlign="center"
            gap="2"
            py="10"
            px="6"
          >
            <Flex
              w="56px"
              h="56px"
              mb="1"
              borderRadius="full"
              bg="rgba(0, 122, 255, 0.08)"
              align="center"
              justify="center"
            >
              <Bell size={24} color="#007AFF" aria-hidden />
            </Flex>
            <Text
              m="0"
              fontSize="15px"
              fontWeight="semibold"
              color="volyx.text"
            >
              No notifications yet
            </Text>
            <Text m="0" fontSize="13px" color="volyx.textMuted">
              Updates about your events, tasks, and messages will show up here.
            </Text>
          </Flex>
        )}
        {!error && items.length > 0 && (
          <Box pb="1">
            {fresh.length > 0 && (
              <NotificationSection
                label="New"
                flush
                collapsing={fresh.every((n) => exitingIds.has(n.id))}
              >
                {renderRows(fresh)}
              </NotificationSection>
            )}
            {earlier.length > 0 && (
              <NotificationSection
                label="Earlier"
                flush
                collapsing={earlier.every((n) => exitingIds.has(n.id))}
              >
                {renderRows(earlier)}
              </NotificationSection>
            )}
          </Box>
        )}
      </Box>

      {/* In the footer rather than the header: on a phone the header has no room left
          beside the title, the "new" count and the close button. */}
      <Flex
        flexShrink={0}
        gap="2"
        px="4"
        pt="3"
        pb={sheet ? 'max(12px, env(safe-area-inset-bottom, 0px))' : '3'}
      >
        {newCount > 0 && (
          <MarkAllReadButton onMarkAll={onMarkAllRead} _hover={{ bg: 'volyx.borderLight' }} />
        )}
        <chakra.button
          type="button"
          // The visible label is short; the accessible name says what "all" is.
          aria-label="See all notifications"
          flex="1"
          minW="0"
          h="40px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          gap="1.5"
          border="none"
          borderRadius="full"
          bg="volyx.primaryLight"
          color="volyx.action"
          fontSize="14px"
          fontWeight="semibold"
          cursor="pointer"
          transition="background-color 0.15s ease, transform 0.1s ease"
          _hover={{ bg: '#DBE7FD' }}
          _active={{ transform: 'scale(0.98)' }}
          _focusVisible={{ outline: '2px solid', outlineColor: 'volyx.action', outlineOffset: '2px' }}
          onClick={onSeeAll}
        >
          See all
          <ArrowRight size={16} aria-hidden />
        </chakra.button>
      </Flex>
    </Flex>
  );
}
